import React, { useEffect, useState } from 'react';
import { useCustomersStore } from '../../../../store/customersStore';
import { Customer, CustomerStatus } from '../../../../services/customersApi';
import styles from './EditCustomerModal.module.css';

interface EditCustomerModalProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
}

export const EditCustomerModal: React.FC<EditCustomerModalProps> = ({ open, customer, onClose }) => {
  const [fullName, setFullName] = useState('');
  const [storeName, setStoreName] = useState('');
  const [llcName, setLlcName] = useState('');
  const [phone, setPhone] = useState('');
  const [address1, setAddress1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [status, setStatus] = useState<CustomerStatus>('active');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const update = useCustomersStore((s) => s.update);

  useEffect(() => {
    if (open && customer) {
      setFullName(customer.full_name || '');
      setStoreName(customer.store_name || '');
      setLlcName(customer.llc_name || '');
      setPhone(customer.contact_phone || '');
      setAddress1(customer.address_line1 || '');
      setCity(customer.city || '');
      setState(customer.state || '');
      setZip(customer.zip_code || '');
      setStatus(customer.customer_status || 'active');
      setIsActive(customer.is_active);
      setError(null);
      setSubmitting(false);
    }
  }, [open, customer]);

  if (!open || !customer) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) { setError('Contact name is required'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await update(customer.id, {
        full_name: fullName.trim(),
        store_name: storeName.trim() || undefined,
        llc_name: llcName.trim() || undefined,
        contact_phone: phone.trim() || undefined,
        address_line1: address1.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        zip_code: zip.trim() || undefined,
        customer_status: status,
        is_active: isActive,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Edit Customer</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Contact Name <span className={styles.required}>*</span></span>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required maxLength={120} />
            </label>
            <label className={styles.field}>
              <span>Store Name</span>
              <input type="text" value={storeName} onChange={(e) => setStoreName(e.target.value)} maxLength={120} />
            </label>
            <label className={styles.field}>
              <span>LLC / Legal Name</span>
              <input type="text" value={llcName} onChange={(e) => setLlcName(e.target.value)} maxLength={120} />
            </label>
            <label className={styles.field}>
              <span>Phone</span>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
            </label>
            <label className={`${styles.field} ${styles.fullWidth}`}>
              <span>Address Line 1</span>
              <input type="text" value={address1} onChange={(e) => setAddress1(e.target.value)} maxLength={200} />
            </label>
            <label className={styles.field}>
              <span>City</span>
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} maxLength={80} />
            </label>
            <label className={styles.field}>
              <span>State</span>
              <input type="text" value={state} onChange={(e) => setState(e.target.value)} maxLength={40} />
            </label>
            <label className={styles.field}>
              <span>ZIP</span>
              <input type="text" value={zip} onChange={(e) => setZip(e.target.value)} maxLength={20} />
            </label>
            <label className={styles.field}>
              <span>Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as CustomerStatus)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </label>
          </div>

          <label className={styles.toggle}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span>Account is active (can log in)</span>
          </label>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomerModal;
